import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { KeyRound, Plus, Trash2, Power } from "lucide-react";

export default function SignupCodesManager() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ code: "", kind: "founder", months: 6, description: "" });
  const [saving, setSaving] = useState(false);

  async function load() {
    try {
      const { data } = await api.get("/admin/signup-codes");
      setItems(data);
    } catch { setItems([]); }
  }
  useEffect(() => { load(); }, []);

  async function create() {
    if (!form.code.trim()) { toast.error("El código no puede estar vacío"); return; }
    setSaving(true);
    try {
      const payload = { code: form.code.trim().toUpperCase(), kind: form.kind, description: form.description };
      if (form.kind === "courtesy") payload.months = Number(form.months) || 6;
      await api.post("/admin/signup-codes", payload);
      toast.success("Código creado");
      setOpen(false);
      setForm({ code: "", kind: "founder", months: 6, description: "" });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al crear");
    } finally { setSaving(false); }
  }

  async function toggle(id) {
    try { await api.patch(`/admin/signup-codes/${id}`); load(); } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  }

  async function remove(id) {
    if (!window.confirm("¿Eliminar este código? Esta acción no se puede deshacer.")) return;
    try { await api.delete(`/admin/signup-codes/${id}`); load(); toast.success("Código eliminado"); } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  }

  return (
    <Card className="border-slate-200" data-testid="signup-codes-card">
      <CardContent className="p-6">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-3">
          <div className="flex items-center gap-2">
            <KeyRound className="h-4 w-4 text-[hsl(var(--accent))]" />
            <p className="overline">Códigos de registro especiales</p>
          </div>
          <Button size="sm" onClick={() => setOpen(true)} data-testid="signup-codes-new-btn">
            <Plus className="h-3 w-3 mr-1" /> Nuevo código
          </Button>
        </div>
        <p className="text-xs text-slate-500 mb-4">
          <strong>Fundador</strong>: sin cobro de por vida (mensualidad ni por profesional), uso único por defecto.
          &nbsp;·&nbsp;
          <strong>Cortesía</strong>: sin cobro durante N meses, reutilizable.
        </p>

        {items.length === 0 ? (
          <p className="text-sm text-slate-500 italic">Aún no hay códigos creados.</p>
        ) : (
          <div className="space-y-2">
            {items.map((c) => (
              <div key={c.id} className="flex items-center justify-between rounded-md border border-slate-200 bg-white px-4 py-3 flex-wrap gap-3" data-testid={`signup-code-row-${c.id}`}>
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-mono font-semibold" style={{ color: "#1E3A8A" }}>{c.code}</p>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full uppercase tracking-wider font-semibold ${
                      c.kind === "founder"
                        ? "bg-amber-100 text-amber-800 border border-amber-200"
                        : "bg-sky-100 text-sky-800 border border-sky-200"
                    }`}>
                      {c.kind === "founder" ? "Fundador · de por vida" : `Cortesía · ${c.months} meses`}
                    </span>
                    {!c.active && <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-200 text-slate-700 uppercase tracking-wider font-semibold">Inactivo</span>}
                  </div>
                  <p className="text-xs text-slate-500 mt-1">
                    {c.description || "—"} · Usos: {c.uses_count}{c.uses_max !== null && c.uses_max !== undefined ? ` / ${c.uses_max}` : " (ilimitado)"}
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Button size="sm" variant="outline" onClick={() => toggle(c.id)} data-testid={`signup-code-toggle-${c.id}`}>
                    <Power className="h-3 w-3 mr-1" /> {c.active ? "Desactivar" : "Activar"}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => remove(c.id)} data-testid={`signup-code-delete-${c.id}`}>
                    <Trash2 className="h-3 w-3 text-red-600" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="signup-code-modal">
          <DialogHeader><DialogTitle>Nuevo código de registro</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Código</Label>
              <Input placeholder="FOUNDER-01" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} data-testid="new-code-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Tipo</Label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setForm({ ...form, kind: "founder" })}
                  className={`rounded-md border px-3 py-2 text-left text-sm transition ${form.kind === "founder" ? "border-[#1E3A8A] bg-[#1E3A8A]/5" : "border-slate-200 hover:border-slate-300"}`}
                  data-testid="new-code-kind-founder"
                >
                  <p className="font-semibold">Fundador</p>
                  <p className="text-xs text-slate-500">Sin cobro de por vida · 1 uso</p>
                </button>
                <button
                  type="button"
                  onClick={() => setForm({ ...form, kind: "courtesy" })}
                  className={`rounded-md border px-3 py-2 text-left text-sm transition ${form.kind === "courtesy" ? "border-[#1E3A8A] bg-[#1E3A8A]/5" : "border-slate-200 hover:border-slate-300"}`}
                  data-testid="new-code-kind-courtesy"
                >
                  <p className="font-semibold">Cortesía</p>
                  <p className="text-xs text-slate-500">Sin cobro N meses · ilimitado</p>
                </button>
              </div>
            </div>
            {form.kind === "courtesy" && (
              <div className="space-y-1.5">
                <Label>Meses de cortesía</Label>
                <Input type="number" min={1} max={36} value={form.months} onChange={(e) => setForm({ ...form, months: e.target.value })} data-testid="new-code-months" />
              </div>
            )}
            <div className="space-y-1.5">
              <Label>Descripción (opcional)</Label>
              <Input placeholder="Fundador #1 · Cliente Beta" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="new-code-desc" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={create} disabled={saving} data-testid="new-code-save">
              {saving ? "Creando…" : "Crear código"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
